import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Global navigation scroll behavior utility
export const useScrollNavigation = () => {
  const location = useLocation();

  useEffect(() => {
    // Disable browser scroll restoration
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
  }, []);

  useEffect(() => {
    const handleRouteChange = () => {
      // Get header offset from CSS variable
      const getHeaderOffset = () => {
        const style = getComputedStyle(document.documentElement);
        const offset = style.getPropertyValue('--header-offset').trim();
        return parseInt(offset.replace('px', '')) || 88;
      };

      const scrollToTarget = (element: Element, headerOffset: number) => {
        const elementTop = element.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementTop - headerOffset;

        window.scrollTo({
          top: Math.max(0, offsetPosition),
          behavior: 'smooth'
        });
      };

      const handleScroll = () => {
        const headerOffset = getHeaderOffset();
        const hash = location.hash;

        if (hash) {
          // Navigate to specific section
          const targetElement = document.querySelector(hash);
          if (targetElement) {
            scrollToTarget(targetElement, headerOffset);
          } else {
            // If hash element doesn't exist yet (lazy loading), try again after delay
            setTimeout(() => {
              const retryElement = document.querySelector(hash);
              if (retryElement) {
                scrollToTarget(retryElement, headerOffset);
              }
            }, 300);
          }
        } else {
          // Navigate to page top
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      };

      // Handle scroll after DOM updates
      requestAnimationFrame(() => {
        handleScroll();
        
        // Handle lazy content shifts (images, fonts)
        setTimeout(handleScroll, 300);
      });
    };

    handleRouteChange();
  }, [location.pathname, location.hash]);
};

// Utility function for programmatic scrolling
export const scrollToSection = (sectionId: string) => {
  const element = document.getElementById(sectionId);
  if (!element) return;

  const style = getComputedStyle(document.documentElement);
  const headerOffset = parseInt(style.getPropertyValue('--header-offset').replace('px', '')) || 88;
  
  const elementTop = element.getBoundingClientRect().top + window.pageYOffset;
  const offsetPosition = elementTop - headerOffset;

  window.scrollTo({
    top: Math.max(0, offsetPosition),
    behavior: 'smooth'
  });
};

// Utility for same-page navigation
export const handleSamePageNavigation = (event: React.MouseEvent<HTMLAnchorElement>) => {
  const href = event.currentTarget.getAttribute('href');
  if (href?.startsWith('#')) {
    event.preventDefault();
    const sectionId = href.substring(1);
    scrollToSection(sectionId);
  }
};